/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RailMetaData } from 'ui/pages/myTv/MainPage';
import { Ducks, MenuUiArea, StoreState, Asset, Specs } from '@wit/vtv-x-foundation-lib';
import { Rail } from 'ui/components';
import { StoreState as LocalStoreState } from 'store/store';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import translate from 'services/i18n/translate';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import { Selectors as GDPRSelectors } from 'core/store/ducks/gdpr';
import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import OptInRail from '../recommendations/OptInRail';
import { useRouter } from '../../../hooks/useRouter';

interface Props {
  rail: RailMetaData;
}

export default function MyTVRecommendation({ rail }: Props) {
  const dispatch = useDispatch();
  const router = useRouter();
  const isOptIn = useSelector(GDPRSelectors.hasAgreedToConsent);
  const [optInClicked, setOptInClicked] = useState(false);
  const [isContinueClicked, setIsContinueClicked] = useState(false);

  const ctx: Ducks.Recommendations.RecommContext = useMemo(
    () => ({
      menuUiArea: MenuUiArea.MY_TV,
      menuItemId: rail.item.id,
    }),
    [rail.item.id]
  );

  const isProcessed = useSelector((state: StoreState) => Ducks.Recommendations.Selectors.isProcessed(state, ctx));

  const recommendationAssets = useSelector((state: LocalStoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(
      state,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECOMMENDATION_RAIL,
      (store) => Ducks.Recommendations.Selectors.assets(store, ctx)
    )
  );

  useEffect(() => {
    dispatch(Ducks.Recommendations.Creators.loadAssets(ctx));
  }, [ctx, dispatch]);

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({ asset, menuItemId });
  };

  if (!isProcessed) {
    return <></>;
  }

  const getRailName = (): string => {
    if (Specs.MENU_ITEM_CONT_WATCHING_NAME.includes(rail.item.name.toLocaleLowerCase())) {
      return translate['mytv.mytvRecommendations1'];
    }
    return translate['mytv.mytvRecommendations2'];
  };

  const getRailBackground = (): RailBackgroundImageName => {
    if (Specs.MENU_ITEM_CONT_WATCHING_NAME.includes(rail.item.name.toLocaleLowerCase())) {
      return RailBackgroundImageName.WATCH_NEXT;
    }
    return RailBackgroundImageName.RELATED_RECOMM;
  };

  const continueClicked = () => {
    setIsContinueClicked(true);
  };

  if (!isOptIn || (isOptIn && !isContinueClicked && optInClicked)) {
    return (
      <OptInRail
        railId={rail.item.id}
        railName={getRailName()}
        railBackgroundImageName={getRailBackground()}
        railDescription={translate['firstTimeUse.recommendations.optIn.invite']}
        onOptInClick={() => setOptInClicked(true)}
        optInClicked={optInClicked}
        continueClicked={continueClicked}
      />
    );
  }
  if (isOptIn && isProcessed && recommendationAssets.length === 0) {
    return (
      <ColdStartRail
        railId={rail.item.id}
        railName={getRailName()}
        railBackgroundImageName={getRailBackground()}
        railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
        className="no-background"
      />
    );
  }
  return (
    <Rail
      railId={rail.item.id}
      menuItemId={rail.item.id}
      className="no-background"
      railName={getRailName()}
      channelId={rail.item.channelId}
      assets={recommendationAssets}
      hasMore={false}
      onCardClick={onCardClick}
    />
  );
}
